import React, { useState, useEffect } from 'react'
import { db } from '../firebase'
import { collection, addDoc, serverTimestamp } from 'firebase/firestore'

const TEAL = '#38beba'
const TEAL_DARK = '#2a9e9a'
const LAUNCH_DATE = new Date('2026-05-18T09:00:00-04:00') // 9am ET May 18

function useCountdown(target) {
  const calc = () => {
    const diff = target - Date.now()
    if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0 }
    return {
      days: Math.floor(diff / 86400000),
      hours: Math.floor((diff % 86400000) / 3600000),
      minutes: Math.floor((diff % 3600000) / 60000),
      seconds: Math.floor((diff % 60000) / 1000),
    }
  }
  const [time, setTime] = useState(calc)
  useEffect(() => {
    const id = setInterval(() => setTime(calc()), 1000)
    return () => clearInterval(id)
  }, [])
  return time
}

const features = [
  {
    icon: '🎯',
    title: 'Lead Generation & Enrichment',
    desc: 'Prospect, import, and enrich leads from multiple sources. AI-assisted scraping, CSV bulk upload, and built-in enrichment — all feeding one clean database.',
  },
  {
    icon: '📧',
    title: 'Outreach Automation & Email Sequences',
    desc: 'Build multi-step email sequences with custom day delays. Schedule campaigns, personalize with tokens, and let the cloud scheduler handle the rest — no browser required.',
  },
  {
    icon: '🗂️',
    title: 'CRM & Sales Pipeline',
    desc: 'Track every deal from first touch to closed. Manage contacts, stages, pipeline value, notes, and tags in one unified system.',
  },
  {
    icon: '⚡',
    title: 'Visual Workflow Builder',
    desc: 'Turn manual tasks into repeatable systems. Build automations with triggers, delays, conditions, and actions — using AI prompts or drag-and-drop logic.',
  },
  {
    icon: '🏷️',
    title: 'White-Label SaaS Resale',
    desc: 'Brand, deploy, and resell the entire platform as your own. Full white-label rights, partner dashboard, Stripe billing, and custom signup links included.',
  },
]

const CountdownUnit = ({ value, label }) => (
  <div style={{ textAlign: 'center', minWidth: 70 }}>
    <div style={{
      background: TEAL,
      color: '#fff',
      borderRadius: 12,
      padding: '12px 8px',
      fontSize: 36,
      fontWeight: 800,
      lineHeight: 1,
      letterSpacing: '-1px',
      minWidth: 64,
    }}>
      {String(value).padStart(2, '0')}
    </div>
    <div style={{ fontSize: 11, color: '#888', marginTop: 6, textTransform: 'uppercase', letterSpacing: 1.5 }}>{label}</div>
  </div>
)

export default function Launchpad() {
  const countdown = useCountdown(LAUNCH_DATE)
  const [email, setEmail] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address.')
      return
    }
    setLoading(true)
    setError('')
    try {
      await addDoc(collection(db, 'launchpad_signups'), {
        email: email.trim().toLowerCase(),
        signedUpAt: serverTimestamp(),
        source: 'launchpad',
      })
      setSubmitted(true)
    } catch (err) {
      console.error('Signup error:', err)
      setError('Something went wrong. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={{ fontFamily: "'Inter', system-ui, sans-serif", background: '#fafaf8', minHeight: '100vh', color: '#1a1a1a' }}>

      {/* Nav */}
      <nav style={{ borderBottom: '1px solid #eee', background: '#fff', position: 'sticky', top: 0, zIndex: 100 }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 24px', height: 60, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 22, fontWeight: 800, color: TEAL, letterSpacing: '-0.5px' }}>Market Genie</span>
            <span style={{ fontSize: 12, background: TEAL, color: '#fff', borderRadius: 6, padding: '2px 8px', fontWeight: 700 }}>2.0</span>
          </div>
          <a
            href="#notify"
            style={{ background: TEAL, color: '#fff', borderRadius: 8, padding: '8px 20px', fontWeight: 700, fontSize: 14, textDecoration: 'none', transition: 'background 0.2s' }}
            onMouseEnter={e => e.target.style.background = TEAL_DARK}
            onMouseLeave={e => e.target.style.background = TEAL}
          >
            Get Notified
          </a>
        </div>
      </nav>

      {/* Hero */}
      <section style={{ maxWidth: 800, margin: '0 auto', padding: '80px 24px 60px', textAlign: 'center' }}>
        <div style={{ display: 'inline-block', background: '#f0fdfc', border: `1.5px solid ${TEAL}`, borderRadius: 100, padding: '6px 18px', fontSize: 13, fontWeight: 600, color: TEAL, marginBottom: 28 }}>
          🚀 &nbsp;Launching May 18, 2026
        </div>
        <h1 style={{ fontSize: 'clamp(36px, 6vw, 64px)', fontWeight: 900, lineHeight: 1.08, letterSpacing: '-2px', marginBottom: 20, color: '#111' }}>
          The AI marketing platform<br />
          <span style={{ color: TEAL }}>built to replace the stack</span><br />
          you're paying too much for.
        </h1>
        <p style={{ fontSize: 20, color: '#555', lineHeight: 1.6, maxWidth: 580, margin: '0 auto 48px' }}>
          Market Genie 2.0 combines lead generation, outreach automation, CRM, workflows, appointment scheduling, and white-label resale — in one system, at a fraction of the cost.
        </p>

        {/* Countdown */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginBottom: 52, flexWrap: 'wrap' }}>
          <CountdownUnit value={countdown.days} label="Days" />
          <CountdownUnit value={countdown.hours} label="Hours" />
          <CountdownUnit value={countdown.minutes} label="Minutes" />
          <CountdownUnit value={countdown.seconds} label="Seconds" />
        </div>

        {/* CTA */}
        <a href="#notify" style={{ display: 'inline-block', background: TEAL, color: '#fff', borderRadius: 10, padding: '16px 40px', fontWeight: 800, fontSize: 18, textDecoration: 'none', letterSpacing: '-0.3px' }}>
          Join the Launch →
        </a>
        <p style={{ fontSize: 13, color: '#aaa', marginTop: 14 }}>No credit card. No obligation. Just a front-row seat.</p>

        {/* Screenshot placeholder */}
        <div style={{ marginTop: 60, borderRadius: 16, border: '2px solid #e5e5e5', background: '#f5f5f0', height: 380, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#bbb', fontSize: 15 }}>
          [ Product screenshot / demo video ]
        </div>
      </section>

      {/* Why we built this */}
      <section style={{ background: '#fff', borderTop: '1px solid #eee', borderBottom: '1px solid #eee' }}>
        <div style={{ maxWidth: 720, margin: '0 auto', padding: '72px 24px', textAlign: 'center' }}>
          <p style={{ fontSize: 13, fontWeight: 700, color: TEAL, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 16 }}>Why we built this</p>
          <h2 style={{ fontSize: 'clamp(26px, 4vw, 40px)', fontWeight: 800, letterSpacing: '-1px', marginBottom: 28, color: '#111' }}>
            Great tools shouldn't cost $2,000/month.
          </h2>
          <p style={{ fontSize: 18, color: '#555', lineHeight: 1.75, marginBottom: 20 }}>
            We built Market Genie because the platforms dominating the market — GoHighLevel, HubSpot, ActiveCampaign — were built for enterprises, priced for enterprises, and bloated for anyone running a lean operation.
          </p>
          <p style={{ fontSize: 18, color: '#555', lineHeight: 1.75, marginBottom: 20 }}>
            Founders, agencies, and operators were paying for 10 tools that didn't talk to each other. They were duct-taping Zapier between systems that were never designed to work together. Something had to change.
          </p>
          <p style={{ fontSize: 18, color: '#555', lineHeight: 1.75 }}>
            Market Genie 2.0 is the full client-acquisition stack — leads to revenue — in one place. AI-assisted, automated, and built to run without you watching it.
          </p>
        </div>
      </section>

      {/* Features */}
      <section style={{ maxWidth: 1100, margin: '0 auto', padding: '72px 24px' }}>
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <p style={{ fontSize: 13, fontWeight: 700, color: TEAL, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 12 }}>What's inside</p>
          <h2 style={{ fontSize: 'clamp(26px, 4vw, 40px)', fontWeight: 800, letterSpacing: '-1px', color: '#111' }}>
            One platform. Every tool you need.
          </h2>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 24 }}>
          {features.map((f, i) => (
            <div key={i} style={{ background: '#fff', border: '1.5px solid #eee', borderRadius: 14, padding: '28px 28px', transition: 'border-color 0.2s, box-shadow 0.2s' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = TEAL; e.currentTarget.style.boxShadow = `0 4px 20px ${TEAL}22` }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#eee'; e.currentTarget.style.boxShadow = 'none' }}
            >
              <div style={{ fontSize: 32, marginBottom: 14 }}>{f.icon}</div>
              <h3 style={{ fontSize: 18, fontWeight: 800, marginBottom: 10, color: '#111', letterSpacing: '-0.3px' }}>{f.title}</h3>
              <p style={{ fontSize: 15, color: '#666', lineHeight: 1.65 }}>{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Founders 50 */}
      <section style={{ background: '#fff', borderTop: '1px solid #eee', borderBottom: '1px solid #eee' }}>
        <div style={{ maxWidth: 820, margin: '0 auto', padding: '72px 24px', textAlign: 'center' }}>
          <div style={{ display: 'inline-block', background: '#fffbea', border: '1.5px solid #f5c842', borderRadius: 100, padding: '6px 18px', fontSize: 13, fontWeight: 700, color: '#b8860b', marginBottom: 24 }}>
            ⚡ Limited to 50 spots
          </div>
          <h2 style={{ fontSize: 'clamp(26px, 4vw, 40px)', fontWeight: 800, letterSpacing: '-1px', marginBottom: 20, color: '#111' }}>
            The Founders 50 Affiliate Program
          </h2>
          <p style={{ fontSize: 18, color: '#555', lineHeight: 1.75, marginBottom: 36 }}>
            The first 50 partners to join get <strong style={{ color: '#111' }}>50% lifetime recurring commission</strong> on every customer they refer — forever. After spot #50, that drops to 20%. This is a one-time opportunity for early believers to build a real revenue stream on top of our growth.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 20, marginBottom: 40, textAlign: 'left' }}>
            {[
              ['🏷️', 'Full White-Label Rights', 'Brand and resell the platform as your own with your name, logo, and domain.'],
              ['💰', '50% Lifetime Commission', 'Earn half of every dollar your referrals generate — for as long as they stay.'],
              ['📦', 'Biz-in-a-Box Kit', 'Sales deck, outreach templates, and marketing content ready to deploy.'],
            ].map(([icon, title, desc], i) => (
              <div key={i} style={{ background: '#fafaf8', border: '1.5px solid #eee', borderRadius: 12, padding: '22px 22px' }}>
                <div style={{ fontSize: 26, marginBottom: 10 }}>{icon}</div>
                <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 8, color: '#111' }}>{title}</div>
                <div style={{ fontSize: 14, color: '#666', lineHeight: 1.6 }}>{desc}</div>
              </div>
            ))}
          </div>
          <a href="#notify" style={{ display: 'inline-block', background: '#111', color: '#fff', borderRadius: 10, padding: '14px 36px', fontWeight: 800, fontSize: 16, textDecoration: 'none' }}>
            Claim a Founders 50 Spot →
          </a>
          <p style={{ fontSize: 13, color: '#aaa', marginTop: 12 }}>$497/yr — includes full white-label access + affiliate enrollment.</p>
        </div>
      </section>

      {/* Email Capture */}
      <section id="notify" style={{ maxWidth: 600, margin: '0 auto', padding: '80px 24px', textAlign: 'center' }}>
        <h2 style={{ fontSize: 'clamp(24px, 4vw, 36px)', fontWeight: 800, letterSpacing: '-1px', marginBottom: 12, color: '#111' }}>
          Be first through the door.
        </h2>
        <p style={{ fontSize: 17, color: '#666', marginBottom: 36, lineHeight: 1.65 }}>
          Drop your email and we'll notify you the moment we go live on May 18. No spam, no fluff — just the launch.
        </p>

        {submitted ? (
          <div style={{ background: '#f0fdfc', border: `2px solid ${TEAL}`, borderRadius: 14, padding: '32px 24px' }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>🎉</div>
            <div style={{ fontWeight: 800, fontSize: 20, color: '#111', marginBottom: 8 }}>You're on the list.</div>
            <div style={{ color: '#666', fontSize: 15 }}>We'll reach out on launch day. See you May 18.</div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={{ display: 'flex', gap: 12, maxWidth: 480, margin: '0 auto', flexWrap: 'wrap' }}>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="your@email.com"
              required
              style={{
                flex: 1,
                minWidth: 200,
                border: '1.5px solid #ddd',
                borderRadius: 10,
                padding: '14px 18px',
                fontSize: 16,
                outline: 'none',
                background: '#fff',
                color: '#111',
              }}
              onFocus={e => e.target.style.borderColor = TEAL}
              onBlur={e => e.target.style.borderColor = '#ddd'}
            />
            <button
              type="submit"
              disabled={loading}
              style={{
                background: loading ? '#aaa' : TEAL,
                color: '#fff',
                border: 'none',
                borderRadius: 10,
                padding: '14px 28px',
                fontWeight: 800,
                fontSize: 16,
                cursor: loading ? 'not-allowed' : 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              {loading ? 'Saving...' : 'Notify Me →'}
            </button>
            {error && <p style={{ width: '100%', color: '#e53e3e', fontSize: 14, marginTop: 4 }}>{error}</p>}
          </form>
        )}
      </section>

      {/* Footer */}
      <footer style={{ borderTop: '1px solid #eee', background: '#fff', padding: '28px 24px', textAlign: 'center' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <span style={{ fontWeight: 800, color: TEAL, fontSize: 16 }}>Market Genie 2.0</span>
          <span style={{ color: '#aaa', fontSize: 13 }}>© 2026 Market Genie · <a href="https://marketgenie.tech" style={{ color: '#aaa', textDecoration: 'none' }}>marketgenie.tech</a> · <a href="mailto:Help@dubdproducts.com" style={{ color: '#aaa', textDecoration: 'none' }}>Help@dubdproducts.com</a></span>
        </div>
      </footer>

    </div>
  )
}
